import { Dispatch, LegacyRef, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import { useGetFilterOptionsQuery } from 'app/api/facade';
import { useAppSelector } from 'app/store/store';
import { Area, PortCatalogData, SingleFilterValue } from 'ais-facade-api/api';
import { useGetAllValue, useFilterComboboxState, useGetInitialFilterFormValue, useGetInitialFilterValues } from 'app/hooks';
import { FormEquality, getVesselsFilterFormEquality } from 'app/helpers/getVesselsFilterFormEquality';

import { Icon, Button, Input, CheckboxOption, CheckboxAll } from 'app/components';

import { Filters } from 'app/types/types';
import { Filters as VesselsCatalogFilters } from 'app/pages/VesselsPage/VesselsPage';

import clsx from 'clsx';
import { AreaInField } from './AreaInField';
import { PortInField } from './PortInField';
import moment from 'moment';
import styles from './FilterPanel.module.scss';

export type FiltersStateT = [
  Filters | VesselsCatalogFilters | null,
  Dispatch<SetStateAction<Filters | VesselsCatalogFilters | null>>,
];
interface FilterPanelI {
  isOpen?: boolean;
  onClose: () => void;
  innerRef?: LegacyRef<HTMLDivElement>;
  filtersState: FiltersStateT;
  place: 'map' | 'catalog';
}
interface OpenState {
  source: boolean;
  type: boolean;
  country: boolean;
  status: boolean;
  constructionDate: boolean;
  sog: boolean;
  cog: boolean;
  heading: boolean;
  draught: boolean;
  eta: boolean;
  destinationPortKey: boolean;
  inPortKey: boolean;
  inAreaCode: boolean;
  fleetGroup: boolean;
}

export interface FilterFormData {
  touched: boolean;
  formEquality?: FormEquality | null;
  fleetOnly: boolean;
  source: string[];
  status: string[];
  country: string[];
  type: string[];
  fleetGroup: string[];
  statusAll: boolean;
  typeAll: boolean;
  countryAll: boolean;
  sourceAll: boolean;
  fleetGroupAll: boolean;

  constructionDate_from: string | undefined;
  constructionDate_to: string | undefined;

  eta_from: string | undefined;
  eta_to: string | undefined;

  sog_min: string | undefined;
  sog_max: string | undefined;

  cog_min: string | undefined;
  cog_max: string | undefined;

  draught_min: string | undefined;
  draught_max: string | undefined;

  heading_min: string | undefined;
  heading_max: string | undefined;

  destinationPortKey: Array<string>;
  inPortKey: Array<string>;
  inAreaCode: Array<string>;

  selectedInPort: PortCatalogData | undefined;
  selectedDestinationPort: PortCatalogData | undefined;
  selectedInArea: Area | undefined;
}

export const FilterPanel = ({ onClose, innerRef, filtersState, place }: FilterPanelI) => {
  const { t } = useTranslation();

  const { data, isError, error, ...filterOptionsRequest } = useGetFilterOptionsQuery();

  const uiConfig = useAppSelector((state) => state.userSlice.uiConfig);

  const allStatuses = useGetAllValue(data?.filter?.status?.values);
  const allSources = useGetAllValue(data?.filter?.source?.values);
  const allTypes = useGetAllValue(data?.filter?.type?.values);
  const allCountries = useGetAllValue(data?.filter?.country?.values);
  const allFleetGroups = useGetAllValue(data?.filter?.fleetGroup?.values);

  const [countryList, setContryList] = useState(data?.filter?.country?.values);
  const [inputValue, setInputValue] = useState<string>('');

  const [filters, setFilters] = filtersState || [];

  const initilOpenState = localStorage.getItem(`${place}OpenFilterMapState`);
  const initStateJson: OpenState =
    typeof initilOpenState === 'string'
      ? JSON.parse(initilOpenState)
      : {
          source: true,
          type: true,
          country: true,
          status: true,
          constructionDate: true,
          sog: true,
          cog: true,
          heading: true,
          draught: true,
          eta: true,
          destinationPortKey: true,
          inPortKey: true,
          inAreaCode: true,
          fleetGroup: true,
        };
  const [openState, setOpenState] = useState<OpenState>(initStateJson);

  const toggleOpen = (aid: keyof OpenState) => {
    setOpenState((prev: OpenState) => {
      if (prev?.[aid]) {
        localStorage.setItem(`${place}OpenFilterMapState`, JSON.stringify({ ...prev, [aid]: false }));

        return { ...prev, [aid]: false };
      } else {
        localStorage.setItem(`${place}OpenFilterMapState`, JSON.stringify({ ...prev, [aid]: true }));

        return { ...prev, [aid]: true };
      }
    });
  };
  useEffect(() => {
    filterOptionsRequest.refetch();
  }, []);

  useEffect(() => {
    if (data?.filter?.country?.values) {
      setContryList(data?.filter?.country?.values);
    }
  }, [data]);

  const activeFiltersAmount = filters?.formEquality
    ? Object.values(filters?.formEquality).filter((el: boolean) => !el)?.length
    : null;
  const initialValues = useGetInitialFilterValues();
  const initialFormValue = useGetInitialFilterFormValue(filtersState);
  const [searchState, setSearchState] = useFilterComboboxState(initialFormValue);

  const handleSubmit = useCallback(
    (formData: FilterFormData) => {
      const { ...initialValuesRaw } = initialValues;
      const { ...formDataRaw } = formData;
      const formEquality = getVesselsFilterFormEquality(initialValuesRaw, formDataRaw);
      const isEqual = Object.values(formEquality).every((el: boolean) => el);
      const {
        fleetOnly,
        source,
        type,
        country,
        status,
        fleetGroup,
        statusAll,
        typeAll,
        countryAll,
        sourceAll,
        fleetGroupAll,
        sog_min,
        sog_max,
        cog_min,
        cog_max,
        inPortKey,
        selectedInPort,
        selectedInArea,
        inAreaCode,
        constructionDate_from,
        constructionDate_to,
        destinationPortKey,
        selectedDestinationPort,
        eta_from,
        eta_to,
        draught_min,
        draught_max,
        heading_min,
        heading_max,
      } = formData;
      const sog =
        (sog_min !== undefined && sog_min !== '') || (sog_max !== undefined && sog_max !== '')
          ? {
              min: sog_min !== undefined && sog_min !== '' ? Number(sog_min) : undefined,
              max: sog_max !== undefined && sog_max !== '' ? Number(sog_max) : undefined,
            }
          : undefined;

      const cog =
        (cog_min !== undefined && cog_min !== '') || (cog_max !== undefined && cog_max !== '')
          ? {
              min: cog_min !== undefined ? Number(cog_min) : undefined,
              max: cog_max !== undefined ? Number(cog_max) : undefined,
            }
          : undefined;

      const heading =
        (heading_min !== undefined && heading_min !== '') || (heading_max !== undefined && heading_max !== '')
          ? {
              min: heading_min !== undefined ? Number(heading_min) : undefined,
              max: heading_max !== undefined ? Number(heading_max) : undefined,
            }
          : undefined;

      const draught =
        (draught_min !== undefined && draught_min !== '') || (draught_max !== undefined && draught_max !== '')
          ? {
              min: draught_min !== undefined ? Number(draught_min) : undefined,
              max: draught_max !== undefined ? Number(draught_max) : undefined,
            }
          : undefined;

      const constructionDate =
        constructionDate_from !== undefined || constructionDate_to !== undefined
          ? {
              from: constructionDate_from ?? undefined,
              to: constructionDate_to ?? undefined,
            }
          : undefined;

      const constructionDateDatetime =
        !isNaN(Number(constructionDate_from)) && (isNaN(Number(constructionDate_to)) || Number(constructionDate_to) === 0)
          ? `${moment().utc().set('year', Number(constructionDate_from)).format('YYYY-01-01T00:00:00Z') + '/..'}`
          : (isNaN(Number(constructionDate_from)) || Number(constructionDate_from) === 0) && !isNaN(Number(constructionDate_to))
            ? `${'../' + moment().utc().set('year', Number(constructionDate_to)).format('YYYY-01-01T00:00:00Z')}`
            : !isNaN(Number(constructionDate_from)) && !isNaN(Number(constructionDate_from))
              ? `${moment().utc().set('year', Number(constructionDate_from)).format('YYYY-01-01T00:00:00Z') + '/' + moment().utc().set('year', Number(constructionDate_to)).format('YYYY-01-01T00:00:00Z')}`
              : undefined;

      const eta =
        eta_from !== undefined || eta_to !== undefined
          ? {
              from: eta_from ?? undefined,
              to: eta_to ?? undefined,
            }
          : undefined;

      const etaDatetime =
        !isNaN(Date.parse(eta_from as string)) && isNaN(Date.parse(eta_to as string))
          ? `${moment.utc(eta_from).format('YYYY-MM-DDTHH:mm:ssZ') + '/..'}`
          : isNaN(Date.parse(eta_from as string)) && !isNaN(Date.parse(eta_to as string))
            ? `${'../' + moment.utc(eta_to).format('YYYY-MM-DDTHH:mm:ssZ')}`
            : !isNaN(Date.parse(eta_from as string)) && !isNaN(Date.parse(eta_to as string))
              ? `${moment.utc(eta_from).format('YYYY-MM-DDTHH:mm:ssZ') + '/' + moment.utc(eta_to).format('YYYY-MM-DDTHH:mm:ssZ')}`
              : undefined;

      const filterData = {
        touched: !isEqual,
        formEquality,
        fleetOnly,
        source: source.map((e) => Number(e)),
        type: type.map((e) => Number(e)),
        country: country.map((e) => Number(e)),
        status: status.map((e) => Number(e)),
        fleetGroup,
        statusAll,
        typeAll,
        countryAll,
        sourceAll,
        fleetGroupAll,
        sog,
        cog,
        heading,
        draught,
        constructionDate,
        constructionDateDatetime,
        eta,
        etaDatetime,
        inPortKey,
        selectedInPort,
        inAreaCode,
        selectedInArea,
        destinationPortKey,
        selectedDestinationPort,
      };
      setFilters(filterData);
      onClose();
    },
    [initialFormValue, filters],
  );

  const validationSchema = Yup.object().shape({
    cog_min: Yup.number()
      .nullable()
      .min(0, t('IncorrectHeadingOrCogValueError'))
      .when('cog_max', (cog_max, schema) => cog_max && schema.max(cog_max, t('FromToValueError')))
      .max(360, t('IncorrectHeadingOrCogValueError')),
    cog_max: Yup.number().min(0, t('IncorrectHeadingOrCogValueError')).max(360, t('IncorrectHeadingOrCogValueError')),

    heading_min: Yup.number()
      .nullable()
      .min(0, t('IncorrectHeadingOrCogValueError'))
      .when('heading_max', (heading_max, schema) => heading_max && schema.max(heading_max, t('FromToValueError')))
      .max(360, t('IncorrectHeadingOrCogValueError')),
    heading_max: Yup.number().min(0, t('IncorrectHeadingOrCogValueError')).max(360, t('IncorrectHeadingOrCogValueError')),

    constructionDate_from: Yup.number()
      .min(0, t('MinZeroValueError'))
      .when(
        'constructionDate_to',
        (constructionDate_to, schema) =>
          (constructionDate_to && schema.max(constructionDate_to, t('FromToValueError'))) ||
          schema.max(moment().year(), `${t('EnteredYearError')} ${moment().year()}`),
      ),
    constructionDate_to: Yup.number()
      .min(0, t('MinZeroValueError'))
      .max(moment().year(), `${t('EnteredYearError')} ${moment().year()}`),

    eta_from: Yup.date().when('eta_to', (eta_to, schema) => eta_to && schema.max(eta_to, t('FromToValueError'))),
    eta_to: Yup.date(),
  });

  const formik = useFormik<FilterFormData>({
    initialValues: initialFormValue,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validationSchema: validationSchema,
  });

  const handleReset = useCallback(() => {
    setSearchState((prev) => ({
      term: {
        ...prev.term,
        inAreaCode: '',
        inPortKey: '',
        destinationPortKey: '',
      },
      visibility: {
        ...prev.visibility,
      },
    }));
    formik.setValues({
      touched: false,
      formEquality: null,
      fleetOnly: typeof uiConfig?.viewportFleetOnly === 'boolean' ? uiConfig?.viewportFleetOnly : false,
      source: allSources || [],
      status: allStatuses || [],
      type: allTypes || [],
      country: allCountries || [],
      fleetGroup: allFleetGroups || [],
      statusAll: true,
      typeAll: true,
      countryAll: true,
      sourceAll: true,
      fleetGroupAll: true,
      constructionDate_from: undefined,
      constructionDate_to: undefined,
      eta_from: undefined,
      eta_to: undefined,

      sog_min: undefined,
      sog_max: undefined,
      cog_min: undefined,
      cog_max: undefined,
      heading_min: undefined,
      heading_max: undefined,
      draught_min: undefined,
      draught_max: undefined,
      destinationPortKey: [],
      inPortKey: [],
      inAreaCode: [],
      selectedInPort: undefined,
      selectedDestinationPort: undefined,
      selectedInArea: undefined,
    });
  }, [allSources, allStatuses, allTypes, allCountries, allFleetGroups, uiConfig]);

  const sourceGroup = useMemo(() => {
    return data?.filter?.source?.values?.map((value) => {
      return (
        <CheckboxOption<FilterFormData>
          key={value.name}
          value={value}
          name="source"
          formValues={formik.values}
          formSetField={formik.setFieldValue}
        />
      );
    });
  }, [data, formik]);

  const checkboxGroupFleetGroup = useMemo(() => {
    return data?.filter?.fleetGroup?.values?.map((fleetGroup) => {
      return (
        <CheckboxOption<FilterFormData>
          disabled={!formik.values.fleetOnly}
          key={fleetGroup.key}
          value={fleetGroup}
          name="fleetGroup"
          formValues={formik.values}
          formSetField={formik.setFieldValue}
        />
      );
    });
  }, [data, allFleetGroups, formik]);

  const checkboxGroupStatus = useMemo(() => {
    return data?.filter?.status?.values?.map((statusValue) => {
      return (
        <CheckboxOption<FilterFormData>
          key={statusValue.name}
          value={statusValue}
          name="status"
          formValues={formik.values}
          formSetField={formik.setFieldValue}
        />
      );
    });
  }, [data, allStatuses, formik]);

  const checkboxGroupType = useMemo(() => {
    return data?.filter?.type?.values?.map((typeValue) => {
      return (
        <CheckboxOption<FilterFormData>
          key={typeValue.name}
          value={typeValue}
          name="type"
          formValues={formik.values}
          formSetField={formik.setFieldValue}
        />
      );
    });
  }, [data, allTypes, formik]);

  const checkboxGroupCountry = useMemo(() => {
    if (formik.values.countryAll === false) {
      const checkedItems: SingleFilterValue[] = [];
      let uncheckedItems: SingleFilterValue[] = [];
      const cl = countryList || [];
      let common: SingleFilterValue[] = [];

      if (cl) {
        for (let i = 0; i < cl.length; i++) {
          for (let j = 0; j < formik.values.country.length; j++) {
            if (cl[i].values?.[0] === Number(formik.values.country[j])) {
              checkedItems.push(cl[i]);
            }
          }
        }
      }

      uncheckedItems = cl.filter((o) => checkedItems.indexOf(o) === -1);
      common = checkedItems.concat(uncheckedItems);

      return common?.map((countryValue) => {
        return (
          <CheckboxOption<FilterFormData>
            key={countryValue.name}
            value={countryValue}
            name="country"
            formValues={formik.values}
            formSetField={formik.setFieldValue}
          />
        );
      });
    } else {
      return countryList?.map((countryValue) => {
        return (
          <CheckboxOption<FilterFormData>
            key={countryValue.name}
            value={countryValue}
            name="country"
            formValues={formik.values}
            formSetField={formik.setFieldValue}
          />
        );
      });
    }
  }, [data, allCountries, formik, countryList]);

  if (isError && 'originalStatus' in error && error.originalStatus === 400) {
    filterOptionsRequest.refetch();
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    const inputValue: string = e.target.value.toLowerCase();

    const filtered = data?.filter?.country?.values?.filter((value) => value?.name?.toLowerCase().includes(inputValue));
    setContryList(filtered);
  };
  const handleRangeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue(e.target.name, e.target.value);
  };

  const indicator = filters?.touched ? <div className={styles.indicator} /> : null;

  const isSubmitButtonDisabled = !formik.isValid;

  useEffect(() => {
    formik.validateForm();
  }, [formik.values]);

  return (
    <div className={styles.root}>
      <div className={styles.dialog_modal} ref={innerRef}>
        <div className={styles.panel_header}>
          <p className={styles.heading}>
            {filters?.touched && <div className={styles.indicator} />}
            {t('FilterPanelHeading')}
            {filters?.touched && filters?.formEquality
              ? ` (${activeFiltersAmount}/${Object.values(filters?.formEquality).length - 1})`
              : ''}
          </p>

          <div onClick={onClose} className={styles.close_button}>
            <Icon size="16" name="close-16" />
          </div>
        </div>
        <div className={styles.form_container}>
          {!data && (
            <div className={styles.loader}>
              <Icon name="spinner-light" size="16" color={`var(--default-primary)`} className={styles.spinner_animate} />
              {t('DataIsLoading')}
            </div>
          )}
          {data && (
            <form onSubmit={formik.handleSubmit} className={styles.form}>
              <div className={styles.container}>
                <div className={styles.filter_container}>
                  <div className={styles.checkboxGroup}>
                    <details open={openState?.['source'] || false} className={styles.details}>
                      <summary
                        className={styles.groupName}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleOpen('source');
                        }}
                      >
                        <Icon name="chevron-down-small" size="14px" className={styles.arrow} />
                        {`${t('DataSourceLabel')}:`}
                        {!filters?.formEquality?.source && indicator}
                      </summary>
                      <div className={styles.details_content}>{sourceGroup}</div>
                    </details>
                  </div>

                  <details open={openState?.['fleetGroup'] || false} className={styles.details}>
                    <summary
                      className={styles.groupName}
                      onClick={(e) => {
                        e.preventDefault();
                        toggleOpen('fleetGroup');
                      }}
                    >
                      <Icon name="chevron-down-small" size="14px" className={styles.arrow} />
                      {`${t('FilterPanelLabelFleetGroup')}:`}
                      {!filters?.formEquality?.fleetGroup && indicator}
                    </summary>
                    <div className={styles.details_content}>
                      <label className={styles.checkboxLabel} aria-disabled={uiConfig?.viewportFleetOnly}>
                        <input
                          id="fleetOnly"
                          type="checkbox"
                          name="fleetOnly"
                          className={styles.checkboxInput}
                          onChange={formik.handleChange}
                          checked={formik.values.fleetOnly}
                          disabled={uiConfig?.viewportFleetOnly}
                        />
                        {t('FilterPanelLabelFleetOnly')}
                      </label>

                      <CheckboxAll<FilterFormData>
                        disabled={!formik.values.fleetOnly}
                        name="fleetGroup"
                        allValues={allFleetGroups}
                        formValues={formik.values}
                        formHandleChange={formik.handleChange}
                        formSetField={formik.setFieldValue}
                      />
                      <div className={styles.listGroup}>{checkboxGroupFleetGroup}</div>
                    </div>
                  </details>
                </div>

                <div className={clsx(styles.checkboxGroup, styles.filter_container)}>
                  <details open={openState?.['status'] || false} className={styles.details}>
                    <summary
                      className={styles.groupName}
                      onClick={(e) => {
                        e.preventDefault();
                        toggleOpen('status');
                      }}
                    >
                      <Icon name="chevron-down-small" size="14px" className={styles.arrow} />
                      {`${t('FilterPanelLabelStatus')}:`}
                      {!filters?.formEquality?.status && indicator}
                    </summary>
                    <div className={styles.details_content}>
                      <CheckboxAll<FilterFormData>
                        name="status"
                        allValues={allStatuses}
                        formValues={formik.values}
                        formHandleChange={formik.handleChange}
                        formSetField={formik.setFieldValue}
                      />
                      <div className={styles.listGroup}>{checkboxGroupStatus}</div>
                    </div>
                  </details>
                </div>

                <div className={clsx(styles.checkboxGroup, styles.filter_container, styles.countryFilter)}>
                  <details open={openState?.['country'] || false} className={styles.details}>
                    <summary
                      className={styles.groupName}
                      onClick={(e) => {
                        e.preventDefault();
                        toggleOpen('country');
                      }}
                    >
                      <Icon name="chevron-down-small" size="14px" className={styles.arrow} />
                      {`${t('FilterPanelLabelCountries')}:`}
                      {!filters?.formEquality?.country && indicator}
                    </summary>
                    <div className={styles.details_content}>
                      <CheckboxAll<FilterFormData>
                        name="country"
                        allValues={allCountries}
                        formValues={formik.values}
                        formHandleChange={formik.handleChange}
                        formSetField={formik.setFieldValue}
                      />
                      <Input
                        type="search"
                        onChange={handleInputChange}
                        placeholder={t('SearchPlaceholder')}
                        className={styles.input}
                        value={inputValue}
                      />
                      <div className={styles.listGroup}>{checkboxGroupCountry}</div>
                    </div>
                  </details>
                </div>
                <div>
                  <div className={clsx(styles.checkboxGroup, styles.filter_container)}>
                    <details open={openState?.['type'] || false} className={styles.details}>
                      <summary
                        className={styles.groupName}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleOpen('type');
                        }}
                      >
                        <Icon name="chevron-down-small" size="14px" className={styles.arrow} />
                        {`${t('ShipTypesLabel')}:`}
                        {!filters?.formEquality?.type && indicator}
                      </summary>
                      <div className={styles.details_content}>
                        <CheckboxAll<FilterFormData>
                          name="type"
                          allValues={allTypes}
                          formValues={formik.values}
                          formHandleChange={formik.handleChange}
                          formSetField={formik.setFieldValue}
                        />
                        <div className={styles.listGroup}>{checkboxGroupType}</div>
                      </div>
                    </details>
                  </div>
                  <div className={styles.filter_container}>
                    <details open={openState?.['sog'] || false} className={styles.details}>
                      <summary
                        className={styles.groupName}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleOpen('sog');
                        }}
                      >
                        <Icon name="chevron-down-small" size="14px" className={styles.arrow} />
                        {`${t('SogFilter')} (${t('VesselSpeedUnit')}):`}
                        {!filters?.formEquality?.sog && indicator}
                      </summary>
                      <div className={styles.details_content}>
                        <div className={styles.from_to_inputs}>
                          <div className={styles.input_wrapper}>
                            {t('FromAsLimit')}:
                            <Input
                              className={styles.input}
                              type="number"
                              name="sog_min"
                              id="sog_min"
                              isNotReal={true}
                              onChange={handleRangeInputChange}
                              value={formik.values['sog_min']}
                              min={0}
                              step={1}
                            />
                          </div>

                          <div className={styles.input_wrapper}>
                            {t('ToAsLimit')}:
                            <Input
                              className={styles.input}
                              type="number"
                              name="sog_max"
                              id="sog_max"
                              isNotReal={true}
                              onChange={handleRangeInputChange}
                              value={formik.values['sog_max']}
                              min={0}
                              step={1}
                            />
                          </div>
                        </div>
                      </div>
                    </details>
                  </div>

                  <div className={styles.filter_container}>
                    <details open={openState?.['cog'] || false} className={styles.details}>
                      <summary
                        className={styles.groupName}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleOpen('cog');
                        }}
                      >
                        <Icon name="chevron-down-small" size="14px" className={styles.arrow} />
                        {`${t('Course')} (${t('DegreesUnit')}):`}
                        {!filters?.formEquality?.cog && indicator}
                      </summary>
                      <div className={styles.details_content}>
                        <div className={styles.from_to_inputs}>
                          <div className={styles.input_wrapper}>
                            {t('FromAsLimit')}:
                            <Input
                              isNotReal={true}
                              className={styles.input}
                              type="number"
                              name="cog_min"
                              id="cog_min"
                              onChange={handleRangeInputChange}
                              value={formik.values['cog_min']}
                              min={0}
                              step={1}
                            />
                          </div>
                          <div className={styles.input_wrapper}>
                            {t('ToAsLimit')}:
                            <Input
                              isNotReal={true}
                              className={styles.input}
                              type="number"
                              name="cog_max"
                              id="cog_max"
                              onChange={handleRangeInputChange}
                              value={formik.values['cog_max']}
                              min={0}
                              step={1}
                            />
                          </div>
                        </div>
                        <div className={styles.error}>
                          {(formik.touched && (formik.errors?.cog_min || formik.errors?.cog_max)) || ' '}
                        </div>
                      </div>
                    </details>
                  </div>

                  <div className={styles.filter_container}>
                    <details open={openState?.['heading'] || false} className={styles.details}>
                      <summary
                        className={styles.groupName}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleOpen('heading');
                        }}
                      >
                        <Icon name="chevron-down-small" size="14px" className={styles.arrow} />
                        {`${t('GyroCompass')} (${t('DegreesUnit')}):`}
                        {!filters?.formEquality?.heading && indicator}
                      </summary>
                      <div className={styles.details_content}>
                        <div className={styles.from_to_inputs}>
                          <div className={styles.input_wrapper}>
                            {t('FromAsLimit')}:
                            <Input
                              className={styles.input}
                              type="number"
                              name="heading_min"
                              id="heading_min"
                              isNotReal={true}
                              onChange={handleRangeInputChange}
                              value={formik.values['heading_min']}
                              min={0}
                              step={1}
                            />
                          </div>
                          <div className={styles.input_wrapper}>
                            {t('ToAsLimit')}:
                            <Input
                              className={styles.input}
                              type="number"
                              name="heading_max"
                              id="heading_max"
                              onChange={handleRangeInputChange}
                              isNotReal={true}
                              value={formik.values['heading_max']}
                              min={0}
                              step={1}
                            />
                          </div>
                        </div>

                        <div className={styles.error}>
                          {(formik.touched && (formik.errors?.heading_min || formik.errors?.heading_max)) || ' '}
                        </div>
                      </div>
                    </details>
                  </div>
                </div>
                <div>
                  <div className={styles.filter_container}>
                    <details open={openState?.['draught'] || false} className={styles.details}>
                      <summary
                        className={styles.groupName}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleOpen('draught');
                        }}
                      >
                        <Icon name="chevron-down-small" size="14px" className={styles.arrow} />
                        {`${t('Draught')} (${t('MeterUnit')}):`}
                        {!filters?.formEquality?.draught && indicator}
                      </summary>
                      <div className={styles.details_content}>
                        <div className={styles.from_to_inputs}>
                          <div className={styles.input_wrapper}>
                            {t('FromAsLimit')}:
                            <Input
                              className={styles.input}
                              type="number"
                              name="draught_min"
                              id="draught_min"
                              isNotReal={true}
                              onChange={handleRangeInputChange}
                              value={formik.values['draught_min']}
                              min={0}
                              step={1}
                            />
                          </div>
                          <div className={styles.input_wrapper}>
                            {t('ToAsLimit')}:
                            <Input
                              className={styles.input}
                              type="number"
                              name="draught_max"
                              id="draught_max"
                              isNotReal={true}
                              onChange={handleRangeInputChange}
                              value={formik.values['draught_max']}
                              min={0}
                              step={1}
                            />
                          </div>
                        </div>
                      </div>
                    </details>
                  </div>

                  <div className={clsx(styles.filter_container, styles.filter_eta)}>
                    <details open={openState?.['eta'] || false} className={styles.details}>
                      <summary
                        className={styles.groupName}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleOpen('eta');
                        }}
                      >
                        <Icon name="chevron-down-small" size="14px" className={styles.arrow} />
                        {`${t('EtaFilterLabel')}:`}
                        {!filters?.formEquality?.eta && indicator}
                      </summary>
                      <div className={styles.details_content}>
                        <div className={styles.from_to_inputs}>
                          <div className={styles.input_wrapper}>
                            {t('FromAsLimit')}:
                            <Input
                              className={styles.input}
                              type="date"
                              name="eta_from"
                              id="eta_from"
                              onChange={handleRangeInputChange}
                              value={formik.values['eta_from']}
                              min={0}
                              step={1}
                            />
                          </div>
                          <div className={styles.input_wrapper}>
                            {t('ToAsLimit')}:
                            <Input
                              className={styles.input}
                              type="date"
                              name="eta_to"
                              id="eta_to"
                              onChange={handleRangeInputChange}
                              value={formik.values['eta_to']}
                              min={0}
                              step={1}
                            />
                          </div>
                        </div>

                        <div className={styles.error}>
                          {(formik.touched && (formik.errors?.eta_from || formik.errors?.eta_to)) || ' '}
                        </div>
                      </div>
                    </details>
                  </div>

                  <div className={styles.filter_container}>
                    <details open={openState?.['inPortKey'] || false} className={styles.details}>
                      <summary
                        className={styles.groupName}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleOpen('inPortKey');
                        }}
                      >
                        <Icon name="chevron-down-small" size="14px" className={styles.arrow} />
                        {`${t('InPortKeyFilter')}:`}
                        {!filters?.formEquality?.inPortKey && indicator}
                      </summary>
                      <div className={styles.details_content}>
                        <PortInField
                          fieldName={'inPortKey'}
                          searchState={[searchState, setSearchState]}
                          onSelect={formik.setFieldValue}
                        />
                      </div>
                    </details>
                  </div>

                  <div className={styles.filter_container}>
                    <details open={openState?.['inAreaCode'] || false} className={styles.details}>
                      <summary
                        className={styles.groupName}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleOpen('inAreaCode');
                        }}
                      >
                        <Icon name="chevron-down-small" size="14px" className={styles.arrow} />
                        {`${t('VesselsInArea')}:`}
                        {!filters?.formEquality?.inAreaCode && indicator}
                      </summary>
                      <div className={styles.details_content}>
                        <AreaInField
                          fieldName="inAreaCode"
                          searchState={[searchState, setSearchState]}
                          onSelect={formik.setFieldValue}
                        />
                      </div>
                    </details>
                  </div>

                  <div className={styles.filter_container}>
                    <details open={openState?.['destinationPortKey'] || false} className={styles.details}>
                      <summary
                        className={styles.groupName}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleOpen('destinationPortKey');
                        }}
                      >
                        <Icon name="chevron-down-small" size="14px" className={styles.arrow} />
                        {`${t('DestinationPortKeyFilter')}:`}
                        {!filters?.formEquality?.destinationPortKey && indicator}
                      </summary>
                      <div className={styles.details_content}>
                        <PortInField
                          fieldName={'destinationPortKey'}
                          searchState={[searchState, setSearchState]}
                          onSelect={formik.setFieldValue}
                        />
                      </div>
                    </details>
                  </div>

                  <div className={styles.filter_container}>
                    <details open={openState?.['constructionDate'] || false} className={styles.details}>
                      <summary
                        className={styles.groupName}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleOpen('constructionDate');
                        }}
                      >
                        <Icon name="chevron-down-small" size="14px" className={styles.arrow} />
                        {`${t('ConstructionDateFilter')}:`}
                        {!filters?.formEquality?.constructionDate && indicator}
                      </summary>
                      <div className={styles.details_content}>
                        <div className={styles.from_to_inputs}>
                          <div className={styles.input_wrapper}>
                            {t('FromAsLimit')}:{' '}
                            <Input
                              className={styles.input}
                              type="number"
                              min={0}
                              name="constructionDate_from"
                              id="constructionDate_from"
                              isNotReal={true}
                              onChange={handleRangeInputChange}
                              value={formik.values['constructionDate_from']}
                            />
                          </div>
                          <div className={styles.input_wrapper}>
                            {t('ToAsLimit')}:{' '}
                            <Input
                              className={styles.input}
                              type="number"
                              min={0}
                              name="constructionDate_to"
                              id="constructionDate_to"
                              isNotReal={true}
                              value={formik.values['constructionDate_to']}
                              onChange={handleRangeInputChange}
                            />
                          </div>
                        </div>
                        <div className={styles.error}>
                          {(formik.touched && (formik.errors?.constructionDate_from || formik.errors?.constructionDate_to)) ||
                            ' '}
                        </div>
                      </div>
                    </details>
                  </div>
                </div>
              </div>
              <div className={styles.form_actions}>
                <Button
                  className={styles.action_button}
                  type="button"
                  label={t('FilterPanelButtonReset')}
                  variant="general"
                  onClick={handleReset}
                />
                <div>
                  <Button
                    disabled={isSubmitButtonDisabled}
                    className={styles.action_button}
                    label={t('FilterPanelButtonSubmit')}
                    variant="general"
                    type="submit"
                  />
                  <Button
                    className={styles.action_button}
                    type="button"
                    label={t('ButtonLabelDiscard')}
                    variant="general"
                    onClick={onClose}
                  />
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
